'use client';

// ContentSourceItem - Individual content source display with actions

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Tooltip from '@mui/material/Tooltip';
import type { ContentSource } from '../../types/content-source';
import type { SourceType } from '@boty/database';
import { formatCharCount } from '../../lib/format';
import { SERVER_DEFAULT_TITLES, stripUrlSuffix } from './contentSourceTitle';

export interface DragHandleProps {
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown> | undefined;
}

export interface ContentSourceItemProps {
  /** The content source to display */
  source: ContentSource;
  /** Callback when delete is clicked */
  onDelete: (id: string) => void;
  /** Callback when edit is clicked (only for text type) */
  onEdit?: ((id: string, content: string) => void) | undefined;
  /** Whether the item is being dragged */
  isDragging?: boolean;
  /** Whether delete is disabled */
  disabled?: boolean;
  /** Drag handle props (from dnd-kit) */
  dragHandleProps?: DragHandleProps;
}

// Type-safe icon mapping (default icons by source type)
const TYPE_ICONS: Record<SourceType, React.ElementType> = {
  text: TextFieldsIcon,
  clarification: QuestionAnswerIcon,
  file: InsertDriveFileIcon,
  image: ImageIcon,
  url: LinkIcon,
  google_sheets: TableChartIcon,
  google_docs: DescriptionIcon,
};

// Type-safe color mapping
const TYPE_COLORS: Record<SourceType, string> = {
  text: 'primary.main',
  clarification: 'warning.main',
  file: 'info.main',
  image: 'success.main',
  url: 'secondary.main',
  google_sheets: '#0F9D58', // Google green
  google_docs: '#4285F4', // Google blue
};

/**
 * Get the file extension from a filename (title)
 */
function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

/**
 * Get file-type specific icon and color for files based on extension
 */
function getFileTypeIcon(filename: string): { icon: React.ElementType; color: string } {
  const ext = getFileExtension(filename);

  switch (ext) {
    case 'pdf':
      return { icon: PictureAsPdfIcon, color: 'error.main' };
    case 'doc':
    case 'docx':
      return { icon: DescriptionIcon, color: 'primary.main' };
    case 'xls':
    case 'xlsx':
      return { icon: TableChartIcon, color: 'success.main' };
    default:
      return { icon: InsertDriveFileIcon, color: 'info.main' };
  }
}

/**
 * Extract image URL from content that contains [Image URL: <url>]
 */
function extractImageUrl(content: string): string | null {
  const match = content.match(/\[Image URL: ([^\]]+)\]/);
  return match ? match[1] ?? null : null;
}

/**
 * Truncate text for preview
 */
function truncateText(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Format relative time for display
 */
function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Known English prefix for clarification source_name stored in DB.
 * Format: "Clarification: {category}"
 */
const CLARIFICATION_PREFIX = 'Clarification: ';

/**
 * Parse clarification content into question and answer parts.
 * Content is stored as "Q: {question}\nA: {answer}"
 */
function parseClarificationContent(content: string): { question: string; answer: string } {
  const match = content.match(/^Q:\s*(.*?)\nA:\s*([\s\S]*)$/);
  if (match) {
    return { question: match[1] ?? '', answer: match[2] ?? '' };
  }
  return { question: '', answer: content };
}

/**
 * Rebuild clarification content from question and answer parts
 */
function buildClarificationContent(question: string, answer: string): string {
  return `Q: ${question}\nA: ${answer}`;
}

/**
 * ContentSourceItem - Display a single content source with type icon, name, status, and actions
 *
 * Features:
 * - Type-specific icon with color
 * - Title and character count
 * - Status indicator (processing, ready, error)
 * - Delete button
 * - Drag handle for reordering
 * - Expandable content preview
 * - RTL support via CSS logical properties
 */
export function ContentSourceItem({
  source,
  onDelete,
  onEdit,
  isDragging = false,
  disabled = false,
  dragHandleProps,
}: ContentSourceItemProps) {
  const t = useTranslations('creator.contentSources');
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingClarification, setIsEditingClarification] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Compute localized display title
  const localizedTitle = (() => {
    const title = stripUrlSuffix(source.title);

    // If the title matches a known server default, replace with localized version
    if (SERVER_DEFAULT_TITLES.has(title)) {
      return t(`defaultTitles.${source.type}`);
    }
    // For clarification type with the English "Clarification: X" prefix, localize the prefix
    if (source.type === 'clarification' && title.startsWith(CLARIFICATION_PREFIX)) {
      const category = title.slice(CLARIFICATION_PREFIX.length);
      return `${t('defaultTitles.clarification')}: ${category}`;
    }
    return title;
  })();

  const isTextEditable = source.type === 'text' && onEdit;
  const isClarificationEditable = source.type === 'clarification';
  const isEditable = isTextEditable || isClarificationEditable;

  const handleClarificationEditClick = () => {
    const { answer } = parseClarificationContent(source.content);
    setEditingAnswer(answer);
    setIsEditingClarification(true);
    setIsExpanded(true);
  };

  const handleClarificationSave = async () => {
    if (!editingAnswer.trim() || isSaving) return;
    setIsSaving(true);
    try {
      const { question } = parseClarificationContent(source.content);
      const newContent = buildClarificationContent(question, editingAnswer);
      const response = await fetch(`/api/bots/${source.botId}/content/${source.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newContent }),
      });
      if (!response.ok) {
        throw new Error('Failed to update content');
      }
      setIsEditingClarification(false);
      queryClient.invalidateQueries({ queryKey: ['contentSources', source.botId] });
      // Also invalidate clarification questions cache so the clarification page reflects edits
      queryClient.invalidateQueries({ queryKey: ['clarification-questions', source.botId] });
    } catch {
      // TODO: surface error to user if needed
      console.error('Failed to save clarification edit');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClarificationCancelEdit = () => {
    setIsEditingClarification(false);
    setEditingAnswer('');
  };

  // For files, get file-type-specific icon; for others use default
  const getIconAndColor = () => {
    if (source.type === 'file') {
      return getFileTypeIcon(source.title);
    }
    return { icon: TYPE_ICONS[source.type], color: TYPE_COLORS[source.type] };
  };

  const { icon: TypeIcon, color: iconColor } = getIconAndColor();

  // For images, extract the thumbnail URL from content
  const imageUrl = source.type === 'image' ? extractImageUrl(source.content) : null;

  // Get status display config
  const getStatusDisplay = () => {
    switch (source.status) {
      case 'processing':
        return {
          icon: <CircularProgress size={16} />,
          color: 'warning.main',
        };
      case 'ready':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />,
          color: 'success.main',
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />,
          color: 'error.main',
        };
      default:
        return null;
    }
  };

  const statusDisplay = getStatusDisplay();

  // Determine border color based on status
  const getBorderColor = () => {
    if (source.status === 'error') return 'error.light';
    if (isDragging) return 'primary.main';
    return 'divider';
  };

  return (
    <Paper
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: getBorderColor(),
        borderRadius: 1,
        backgroundColor: isDragging ? 'action.hover' : 'background.paper',
        opacity: isDragging ? 0.9 : 1,
        transition: 'box-shadow 0.2s, opacity 0.2s',
      }}
    >
      {/* Main row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Drag handle */}
        {dragHandleProps && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
            }}
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}

        {/* Type icon or image thumbnail */}
        <Box sx={{ flexShrink: 0 }}>
          {source.type === 'image' && imageUrl ? (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 0.5,
                overflow: 'hidden',
                backgroundColor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={source.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <TypeIcon sx={{ color: iconColor, fontSize: 20 }} />
          )}
        </Box>

        {/* Content info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {(source.type === 'google_docs' || source.type === 'google_sheets') && source.sourceUrl ? (
            /* Google Docs/Sheets: clickable title, no URL subtitle */
            <Typography
              component="a"
              href={source.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              fontWeight="medium"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {localizedTitle}
            </Typography>
          ) : (
            /* All other types: normal title, allow 2 lines for URLs */
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...(source.type === 'url'
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'normal',
                    }
                  : {
                      whiteSpace: 'nowrap',
                    }),
              }}
            >
              {localizedTitle}
            </Typography>
          )}
          {source.type === 'url' && source.sourceUrl && (
            <Typography
              component="a"
              href={source.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline', color: 'primary.main' },
              }}
            >
              {source.sourceUrl}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {formatCharCount(source.characterCount)} {t('actions.characters')}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
              · {formatRelativeTime(source.updatedAt)}
            </Typography>
            {source.refreshEnabled && source.lastRefreshedAt && (
              <Tooltip
                title={
                  source.lastRefreshStatus === 'failed'
                    ? source.lastRefreshError || t('refreshFailed')
                    : t('lastRefreshed')
                }
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <AutorenewIcon
                    sx={{
                      fontSize: 14,
                      color:
                        source.lastRefreshStatus === 'failed'
                          ? 'error.main'
                          : 'success.main',
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={
                      source.lastRefreshStatus === 'failed'
                        ? 'error.main'
                        : 'text.disabled'
                    }
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {formatRelativeTime(source.lastRefreshedAt)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Status indicator */}
        {statusDisplay && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {statusDisplay.icon}
          </Box>
        )}

        {/* Expand/collapse button */}
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? t('actions.collapse') : t('actions.expand')}
          sx={{ flexShrink: 0 }}
        >
          {isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>

        {/* Edit button (for text and clarification content) */}
        {isEditable && (
          <IconButton
            size="small"
            onClick={() => {
              if (isClarificationEditable) {
                handleClarificationEditClick();
              } else if (isTextEditable) {
                onEdit!(source.id, source.content);
              }
            }}
            disabled={disabled || source.status === 'processing' || isSaving}
            aria-label={t('actions.edit')}
            sx={{
              flexShrink: 0,
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}

        {/* Delete button */}
        <IconButton
          size="small"
          onClick={() => onDelete(source.id)}
          disabled={disabled || source.status === 'processing'}
          aria-label={t('actions.delete')}
          sx={{
            flexShrink: 0,
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Expanded content preview */}
      <Collapse in={isExpanded}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {source.type === 'clarification' && isEditingClarification ? (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 'medium' }}
              >
                {parseClarificationContent(source.content).question}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={editingAnswer}
                onChange={(e) => setEditingAnswer(e.target.value)}
                disabled={isSaving}
                size="small"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClarificationCancelEdit}
                  disabled={isSaving}
                >
                  {t('actions.cancel')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleClarificationSave}
                  disabled={!editingAnswer.trim() || isSaving}
                  startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {t('actions.save')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                backgroundColor: 'action.hover',
                p: 1,
                borderRadius: 1,
                fontSize: '0.75rem',
                lineHeight: 1.5,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 150,
                overflow: 'auto',
              }}
            >
              {truncateText(source.content, 500)}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default ContentSourceItem;
